// Company information from Quanentry Capital
export const companyInfo = {
  name: "Quanentry Capital Pvt. Ltd.",
  tagline: "Precision Trading, Powered By Innovation",
  cin: "U64990HR2025PTC137935",
  registeredOffice: "H No. 944, Block C, Sushant Lok Phase I, Gurugram – 122001, Haryana, India",
  phone: "+918178643947",
  email: "contact@quanentry.com",
  careersEmail: "careers@quanentry.com",
  ceoEmail: "contact@quanentry.com",
  logo: "/brand/quanentry-logo-official.png",
  socialMedia: {
    facebook: "https://www.facebook.com/Quanentry/",
    instagram: "https://www.instagram.com/quanentry/",
    linkedin: "https://www.linkedin.com/company/quanentry/",
    twitter: "https://x.com/quanentry"
  }
};

export const navigation = [
  { label: "Home", path: "/" },
  { label: "About Us", path: "/about" },
  { label: "Compliance", path: "/compliance" },
  { label: "Careers", path: "/careers" },
  { label: "Contact", path: "/contact" }
];

export const heroContent = {
  headline: "Engineering Systematic Market Intelligence Through Quantitative Research, Algorithms, and Disciplined Capital Deployment.",
  subheadline: "Quanentry Capital Private Limited develops quantitative research, mathematical models, AI-driven trading systems, and advanced market intelligence to systematically deploy its own capital across regulated financial markets."
};

export const aboutContent = {
  title: "About Quanentry Capital",
  introduction: "Quanentry Capital Private Limited develops quantitative research, mathematical models, and proprietary trading technologies to engineer systematic market intelligence and deploy its own capital across regulated financial markets.",
  whoWeAre: "Quanentry Capital Private Limited is an institutional quantitative research and trading firm specializing in systematic capital deployment across regulated financial markets. Through statistical validation, rigorous mathematical modeling, and engineering excellence, we drive continuous system improvement and disciplined risk management across our proprietary trading infrastructure.",
  mission: "Our mission is to continuously advance our proprietary research ecosystem by integrating mathematical excellence, cutting-edge technology, and disciplined risk management, fostering an engineering culture dedicated to building long-term institutional capability.",
  sections: [
    {
      title: "Proprietary Trading Model",
      content: "We deploy our own capital across regulated financial markets using systematically validated quantitative strategies. Operating without external client funds ensures complete alignment between research innovation and capital deployment."
    },
    {
      title: "Engineering-First DNA",
      content: "Our core foundation rests on robust software engineering, scalable algorithmic pipelines, and high-performance execution infrastructure. Every model is built with mathematical precision, modularity, and strict automated risk controls."
    },
    {
      title: "Quantitative Research",
      content: "We prioritize rigorous statistical modeling, market microstructure research, machine learning, and quantitative empirical testing over subjective analysis, maintaining a sustained research edge."
    },
    {
      title: "Compliance-First Philosophy",
      content: "Regulatory compliance and institutional governance are embedded into our daily operational architecture, maintaining full adherence to Indian Companies Act (MCA), SEBI directives, RBI guidelines, and statutory regulations."
    }
  ],
  ceoMessage: {
    title: "CEO Desk",
    content: "At Quanentry Capital Private Limited, we view India's expanding financial markets as a fertile environment for rigorous mathematical research and technological innovation. Our focus is centered on building a resilient, research-led institution powered by disciplined quantitative thinking, robust engineering, and comprehensive risk management. Rather than pursuing speculative outcomes, we invest systematically in data infrastructure, algorithmic validation, and human capital to ensure responsible, continuous innovation and sustainable capital deployment.",
    name: "Mr. Aditya Jaiswal",
    designation: "Founder & CEO",
    linkedin: "https://www.linkedin.com/in/aditya-jaiswal7083/",
    image: "/brand/aditya-jaiswal-ceo.png"
  },
  legalStatement: "Quanentry Capital Private Limited conducts all trading activities exclusively using its own proprietary capital. The Company does not solicit investments, accept client funds, provide investment advisory services, or offer portfolio management services. All activities are carried out within the applicable regulatory framework and remain subject to market risks."
};

export const technologyStack = {
  title: "Technology Infrastructure",
  description: "Our proprietary quant models, advanced mathematical strategies, and technology-driven research give us a competitive edge, enabling precision trading with transparency and efficiency.",
  categories: [
    {
      category: "Programming Languages",
      description: "High-performance languages for trading systems",
      technologies: [
        { name: "Python", desc: "Research & ML", level: 95 },
        { name: "C++20", desc: "Low-latency core", level: 90 },
        { name: "Rust", desc: "Memory-safe systems", level: 85 },
        { name: "TypeScript", desc: "Frontend & tooling", level: 88 },
        { name: "Go", desc: "Microservices", level: 80 },
        { name: "Julia", desc: "Numerical computing", level: 75 }
      ]
    },
    {
      category: "Machine Learning & AI",
      description: "Cutting-edge ML frameworks for alpha generation",
      technologies: [
        { name: "PyTorch", desc: "Deep learning", level: 95 },
        { name: "TensorFlow", desc: "Production ML", level: 90 },
        { name: "scikit-learn", desc: "Classical ML", level: 92 },
        { name: "XGBoost", desc: "Gradient boosting", level: 88 },
        { name: "LightGBM", desc: "Fast training", level: 85 },
        { name: "CUDA/cuDNN", desc: "GPU acceleration", level: 80 }
      ]
    },
    {
      category: "Data & Databases",
      description: "Scalable storage for tick data and analytics",
      technologies: [
        { name: "TimescaleDB", desc: "Time-series data", level: 95 },
        { name: "PostgreSQL", desc: "Relational data", level: 90 },
        { name: "Redis", desc: "In-memory cache", level: 92 },
        { name: "Apache Parquet", desc: "Columnar storage", level: 88 },
        { name: "ClickHouse", desc: "OLAP analytics", level: 85 },
        { name: "MongoDB", desc: "Document store", level: 82 }
      ]
    },
    {
      category: "Infrastructure & DevOps",
      description: "Enterprise-grade deployment and orchestration",
      technologies: [
        { name: "Kubernetes", desc: "Container orchestration", level: 95 },
        { name: "Docker", desc: "Containerization", level: 92 },
        { name: "AWS", desc: "Cloud infrastructure", level: 90 },
        { name: "Terraform", desc: "Infrastructure as code", level: 88 },
        { name: "Prometheus", desc: "Monitoring", level: 85 },
        { name: "Bare-metal", desc: "Co-located servers", level: 80 }
      ]
    },
    {
      category: "Messaging & Streaming",
      description: "Real-time data pipelines and event processing",
      technologies: [
        { name: "Apache Kafka", desc: "Event streaming", level: 95 },
        { name: "ZeroMQ", desc: "Low-latency messaging", level: 90 },
        { name: "WebSocket", desc: "Real-time feeds", level: 88 },
        { name: "gRPC", desc: "RPC framework", level: 85 },
        { name: "RabbitMQ", desc: "Message broker", level: 82 },
        { name: "NATS", desc: "Cloud messaging", level: 78 }
      ]
    },
    {
      category: "Custom Systems",
      description: "Proprietary trading infrastructure",
      technologies: [
        { name: "Execution Engine", desc: "Ultra-low latency", level: 98 },
        { name: "OMS/EMS", desc: "Order management", level: 95 },
        { name: "Risk Engine", desc: "Real-time monitoring", level: 92 },
        { name: "Alpha Platform", desc: "Strategy research", level: 90 },
        { name: "Market Data", desc: "Feed handlers", level: 88 },
        { name: "Compliance", desc: "Regulatory engine", level: 85 }
      ]
    }
  ]
};

export const complianceContent = {
  title: "Compliance & Legal Framework",
  introduction: "Quanentry Capital Private Limited operates within a strict corporate governance and regulatory compliance architecture. Compliance is systematically engineered into our technological infrastructure, research workflows, and operational procedures, ensuring full statutory adherence and institutional transparency across all market activities.",
  structure: {
    title: "Company Legal Structure",
    content: "Quanentry Capital Private Limited is a private limited company incorporated under the Companies Act, 2013. The Company operates in full compliance with applicable Indian laws and adheres to a governance-first framework across its corporate structure. Activities requiring specific statutory registrations are undertaken exclusively where legally mandated."
  },
  philosophy: {
    title: "Compliance Philosophy",
    content: "Compliance is embedded into every stage of our organization, including quantitative research, technology development, corporate governance, operational execution, and documentation. We view regulatory compliance and systemic auditability as strategic institutional strengths that guarantee accountability, transparency, and operational discipline."
  },
  regulations: {
    title: "Regulatory Alignment",
    items: [
      {
        regulation: "Ministry of Corporate Affairs (MCA)",
        description: "Full compliance with the Companies Act, 2013, including statutory corporate filings, board oversight, financial disclosures, and governance standards."
      },
      {
        regulation: "Securities and Exchange Board of India (SEBI)",
        description: "All market participation activities are conducted strictly in accordance with applicable SEBI regulations, exchange operational rules, and statutory directives where relevant."
      },
      {
        regulation: "Reserve Bank of India (RBI)",
        description: "Strict adherence to applicable banking guidelines, institutional capital flow frameworks, and financial transaction parameters."
      },
      {
        regulation: "Foreign Exchange Management Act (FEMA)",
        description: "Full compliance with FEMA guidelines and statutory cross-border transaction rules where applicable to company operations."
      }
    ]
  },
  complianceByDesign: {
    title: "Compliance by Design",
    subtitle: "Integrating regulatory adherence and operational discipline across every structural layer",
    items: [
      {
        title: "Governance & Oversight",
        desc: "Structured board supervision, clear operational boundaries, and documented executive approvals across all firm activities."
      },
      {
        title: "Research & Modeling",
        desc: "Empirical validation, reproducible quantitative methodologies, and strict algorithmic verification prior to live deployment."
      },
      {
        title: "Technology & Systems",
        desc: "Automated pre-trade risk controls, system boundary limits, and continuous algorithmic execution surveillance."
      },
      {
        title: "Operational Workflows",
        desc: "Segregated operational duties, standardized execution protocols, and continuous risk monitoring."
      },
      {
        title: "Documentation & Audit",
        desc: "Comprehensive decision logging, automated execution trails, and searchable historical record-keeping."
      },
      {
        title: "Internal Review",
        desc: "Periodic internal compliance audits, statutory review cycles, and ongoing policy evaluation."
      }
    ]
  },
  pillars: {
    title: "Compliance Pillars",
    subtitle: "Structural principles governing our internal operations",
    items: [
      { title: "Governance", desc: "Clear corporate oversight, board supervision, and structured decision-making processes." },
      { title: "Regulatory Compliance", desc: "Strict adherence to statutory requirements, market rules, and legal mandates." },
      { title: "Information Security", desc: "Protecting proprietary research, market data pipelines, and infrastructure access controls." },
      { title: "Documentation & Audit Trail", desc: "Comprehensive record-keeping, decision logging, and system traceability." },
      { title: "Internal Controls", desc: "Automated pre-trade risk controls, system boundary checks, and operational surveillance." },
      { title: "Integrity", desc: "Uncompromising commitment to ethical conduct and market fairness across all activities." }
    ]
  },
  disclaimers: [
    "Quanentry Capital Private Limited does not provide investment advisory services, research reports for retail distribution, or trading recommendations to third parties.",
    "The Company does not operate as a Portfolio Manager (PMS), Alternative Investment Fund (AIF), or collective investment scheme.",
    "The Company does not solicit, accept, handle, or manage public, retail, or client funds in any capacity.",
    "All trading and market participation activities are conducted exclusively using the Company's proprietary capital.",
    "The Company does not offer guaranteed returns, fixed-yield investment products, or wealth management services.",
    "All financial market participation involves capital risk and is subject to prevailing market conditions and regulatory frameworks.",
    "Information published on this website is provided strictly for general corporate verification and informational purposes only."
  ],
  escalation: {
    title: "Official Regulatory Channels",
    description: "For official regulatory inquiries or statutory correspondence, communication may be directed through official corporate channels.",
    ceo: {
      name: "Aditya Jaiswal",
      email: "contact@quanentry.com",
      phone: "9229874980"
    },
    workingHours: "Monday to Friday between 10:00 am to 6:00 pm",
    regulatoryEscalation: "In the absence of resolution, official complaints or regulatory inquiries may be directed to SEBI, NSE, or BSE through their respective official grievance redressal portals."
  }
};

export const careersContent = {
  title: "Join Our Team At Quanentry Capital",
  introduction: "Join our team at Quanentry Capital Pvt. Ltd., a technology-driven proprietary trading firm built on innovation, precision, and performance. We offer a fast-paced, research-oriented environment where talented individuals can grow, learn, and make a tangible impact on financial markets.",
  whyWorkWithUs: {
    title: "Why Work With Us?",
    content: "At Quanentry Capital Pvt Ltd, we provide a fast-paced, technology-driven proprietary trading environment where each day brings new challenges and learning opportunities. Team members gain hands-on experience in financial markets, collaborate with seasoned quants and analysts, and have the freedom to implement in-house mathematical and research-driven strategies."
  },
  culture: {
    title: "Our Culture",
    values: [
      "Mission-driven: We solve hard problems in systematic trading",
      "High IQ environment: Surrounded by exceptional engineers and researchers",
      "Deep work: Focus on meaningful research and engineering challenges",
      "Continuous learning: Access to cutting-edge technology and research",
      "Direct Impact: Your work directly contributes to measurable results and company growth"
    ]
  },
  roles: [
    {
      title: "Quantitative Researcher",
      description: "Develop alpha-generating strategies using advanced mathematics and machine learning."
    },
    {
      title: "Machine Learning Engineer",
      description: "Build and deploy production ML systems for trading and risk management."
    },
    {
      title: "Algorithmic Trader",
      description: "Design and implement systematic trading strategies."
    },
    {
      title: "Low-Latency Systems Engineer",
      description: "Design ultra-fast execution systems in C++ and Rust."
    },
    {
      title: "Risk Engineer",
      description: "Develop real-time risk management and surveillance systems."
    },
    {
      title: "Compliance Technologist",
      description: "Build automated compliance and regulatory reporting systems."
    },
    {
      title: "Data Scientist",
      description: "Extract insights from market data and build predictive models."
    }
  ],
  applyMessage: "If you are passionate about quantitative finance and world-class engineering, we'd love to hear from you. Send your resume to careers@quanentry.com or apply directly through our website."
};

export const contactInfo = {
  title: "Corporate Communications",
  subtitle: "For official corporate communication, recruitment enquiries, technology partnerships, regulatory correspondence, vendor discussions and business-related enquiries.",
  registeredOffice: {
    label: "Registered Office Address",
    value: "944, Block C, Sushant Lok Phase 1, Gurugram, Haryana, 122001"
  },
  cin: {
    label: "Corporate Identity Number (CIN)",
    value: "U64990HR2025PTC137935"
  },
  email: {
    label: "Official Email",
    value: "contact@quanentry.com"
  },
  phone: {
    label: "Phone Number",
    value: "+918178643947"
  },
  workingHours: {
    label: "BUSINESS HOURS",
    value: "Monday – Friday\n10:00 AM – 6:00 PM IST"
  },
  disclaimer: "Your information is transmitted securely and handled in accordance with Quanentry Capital's Privacy Policy.",
  escalationMatrix: {
    title: "Official Regulatory Channels",
    description: "In accordance with institutional transparency and statutory governance, official regulatory links and grievance portals are provided below.",
    ceo: {
      name: "Aditya Jaiswal",
      title: "Founder & CEO",
      email: "contact@quanentry.com",
      phone: "+91 9229874980"
    },
    availability: "Monday – Friday: 10:00 AM – 6:00 PM IST",
    escalateTitle: "Official Regulatory Portals",
    escalateDescription: "For formal regulatory grievances or statutory inquiries, visitors may access official market infrastructure and regulatory channels directly.",
    regulatoryBodies: [
      { name: "SEBI", url: "https://scores.sebi.gov.in" },
      { name: "NSE", url: "https://investorhelpline.nseindia.com/NICEPLUS/" },
      { name: "BSE", url: "https://bsecrs.bseindia.com/ecomplaint/frmInvestorHome.aspx" },
      { name: "CDSL", url: "https://www.cdslindia.com/Footer/grievances.aspx" },
      { name: "MCX", url: "https://www.mcxindia.com/Investor-Services" },
      { name: "NCDEX", url: "https://www.ncdex.com/investor-grievances/complaint-registration-form" }
    ],
    note: "Please retain your Quanentry Service Ticket or Enquiry Reference Number before approaching any regulatory authority. This assists in faster review and correspondence."
  }
};

export const faqContent = [
  {
    question: "What is proprietary trading?",
    answer: "Proprietary trading at Quanentry Capital Pvt Ltd involves deploying our own capital to trade Indian securities, derivatives, and digital assets as permitted by law. We leverage in-house mathematical strategies and quantitative models to generate profits without using client funds."
  },
  {
    question: "Who can apply for careers?",
    answer: "Quants, traders, analysts, and interns driven to innovate in financial markets can apply through our Careers page with a detailed application form."
  },
  {
    question: "How does Quanentry Capital grow?",
    answer: "We grow by developing proprietary quantitative models, conducting rigorous mathematical research, and strategically expanding our capital. Our aim is to set global benchmarks while creating high-quality jobs in India's financial sector."
  },
  {
    question: "What makes Quanentry Capital unique?",
    answer: "Our proprietary quant models, advanced mathematical strategies, and technology-driven research give us a competitive edge, enabling precision trading with transparency and efficiency."
  }
];
